import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { API, imageUrl } from "../lib/api";
import UploadDropzone from "./UploadDropzone";
import PartDetailDialog from "./PartDetailDialog";
import ModelSelect from "./ModelSelect";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { Loader2, Search, Zap, ImageOff, ScanText, Sparkles, Images, CheckCircle2, Scissors, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export const confidenceStyle = (c) => {
  switch (c) {
    case "HIGH CONFIDENCE": return "bg-green-600 text-white";
    case "GOOD MATCH": return "bg-blue-600 text-white";
    case "POSSIBLE MATCH": return "bg-amber-500 text-white";
    default: return "bg-zinc-400 text-white";
  }
};

const formatMrp = (v) => {
  if (v == null) return null;
  try { return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(v); }
  catch { return String(v); }
};

const SignalBar = ({ label, value }) => (
  <div className="flex items-center gap-2">
    <span className="text-[9px] uppercase tracking-wider w-9 text-muted-foreground">{label}</span>
    <div className="h-1 flex-1 bg-secondary rounded-full overflow-hidden">
      <div className="h-full bg-foreground/70 rounded-full" style={{ width: `${value}%` }} />
    </div>
    <span className="text-[9px] font-mono w-8 text-right">{value}</span>
  </div>
);

const ResultCard = ({ r, onClick, showScores }) => {
  const [err, setErr] = useState(false);
  const primaryId = (r.image_ids && r.image_ids[0]) || r.best_image_id;
  const imgCount = (r.image_ids && r.image_ids.length) || (r.best_image_id ? 1 : 0);
  const models = r.models || [];

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: Math.min(r.rank * 0.03, 0.4) }}
      data-testid={`result-card-${r.rank}`}
      className="group text-left border rounded-md overflow-hidden bg-card hover:border-brand transition-colors flex flex-col"
    >
      <button onClick={onClick} className="relative aspect-square bg-white flex items-center justify-center p-4 overflow-hidden w-full">
        {r.model_compatible && (
          <span className="absolute bottom-2 left-2 z-10 flex items-center gap-1 bg-green-600 text-white text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm">
            <CheckCircle2 className="w-3 h-3" /> Model fit
          </span>
        )}
        {!err && primaryId ? (
          <img src={imageUrl(primaryId)} alt={r.part_no} loading="lazy" onError={() => setErr(true)}
            className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-105" />
        ) : (
          <ImageOff className="w-8 h-8 text-muted-foreground" />
        )}
      </button>

      <div className="p-3 border-t space-y-1.5 flex flex-col flex-1">
        <div className="flex items-center justify-between gap-2">
          <div className="font-mono text-sm font-bold text-brand truncate" data-testid={`result-partno-${r.rank}`}>{r.part_no}</div>
          <span className="shrink-0 text-[11px] font-semibold px-1.5 py-0.5 rounded bg-green-100 text-green-700" data-testid={`match-pct-${r.rank}`}>
            {r.similarity}%
          </span>
        </div>

        {r.description ? (
          <div className="text-xs text-foreground line-clamp-2 leading-snug min-h-[2rem] uppercase">{r.description}</div>
        ) : (
          <div className="text-[11px] text-muted-foreground/60 italic min-h-[2rem]">No description available</div>
        )}

        <div className="text-[11px] leading-snug">
          {models.length > 0 ? (
            <span className="text-brand font-medium">{models.join(", ")}</span>
          ) : (
            <span className="text-muted-foreground/60">Models: —</span>
          )}
        </div>

        <div className="flex items-center justify-between pt-1">
          {r.mrp != null ? (
            <span className="font-mono text-sm font-bold text-foreground">MRP: ₹{formatMrp(r.mrp)}</span>
          ) : (
            <span className="text-[11px] text-muted-foreground/60">MRP: —</span>
          )}
          <span className="text-[10px] text-muted-foreground flex items-center gap-1">
            <Images className="w-3 h-3" />{imgCount}
          </span>
        </div>

        {showScores && (
          <div className="space-y-2 pt-2 border-t mt-1" data-testid={`breakdown-${r.rank}`}>
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Visual Similarity</span>
              <span className="font-mono text-sm font-bold text-brand">{r.similarity}%</span>
            </div>
            {r.breakdown && (
              <>
                <SignalBar label="CLIP" value={r.breakdown.clip} />
                <SignalBar label="DINO" value={r.breakdown.dino} />
                <SignalBar label="GEO" value={r.breakdown.geom} />
                <SignalBar label="OCR" value={r.breakdown.ocr} />
              </>
            )}
            <div className="flex items-center justify-between pt-1">
              <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-sm ${confidenceStyle(r.confidence)}`}
                data-testid={`confidence-${r.rank}`}>
                {r.confidence}
              </span>
              {r.ocr_exact && (
                <span className="flex items-center gap-1 text-[9px] text-green-600 uppercase tracking-wider">
                  <ScanText className="w-3 h-3" /> P/N match
                </span>
              )}
            </div>
          </div>
        )}

        <Button variant="outline" size="sm" onClick={onClick} data-testid={`view-photos-${r.rank}`}
          className="w-full mt-auto gap-1.5 text-xs h-8">
          <Images className="w-3.5 h-3.5" /> View all photos{imgCount > 1 ? ` (${imgCount})` : ""}
        </Button>
      </div>
    </motion.div>
  );
};

const VisualSearch = ({ showDebug = false, allowModeSelect = false, showScores = false, allowModel = true }) => {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [topN, setTopN] = useState("20");
  const [threshold, setThreshold] = useState(50);
  const [mode, setMode] = useState("AUTO");
  const [model, setModel] = useState(null);
  const [removeBg, setRemoveBg] = useState(false);
  const [modes, setModes] = useState({});
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [debug, setDebug] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios.get(`${API}/config`).then((r) => {
      setModes(r.data.modes || {});
      setMode(r.data.default_mode || "AUTO");
    }).catch(() => {});
  }, []);

  const onFile = (f) => { setFile(f); setPreviewUrl(URL.createObjectURL(f)); setResults(null); setDebug(null); };
  const onClear = () => { setFile(null); setPreviewUrl(null); setResults(null); setDebug(null); };

  const runSearch = async () => {
    if (!file) return;
    setLoading(true);
    setResults(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("top_n", topN);
      fd.append("threshold", String(threshold));
      fd.append("mode", mode);
      if (allowModel && model) fd.append("model", model);
      fd.append("remove_bg", removeBg ? "true" : "false");
      const customerToken = localStorage.getItem("partvision_token");
      const adminToken = localStorage.getItem("mpf_token");
      const token = customerToken || adminToken;

      const config = token
        ? { headers: { Authorization: `Bearer ${token}` } }
        : {};

      const { data } = await axios.post(`${API}/search`, fd, config);
      setResults(data.results);
      setDebug(data.debug);
      if (data.results.length === 0) toast.info("No parts above the similarity threshold. Try lowering it.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Search failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-10">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <div className="md:col-span-8">
          <UploadDropzone file={file} previewUrl={previewUrl} onFile={onFile} onClear={onClear} />
        </div>
        <div className="md:col-span-4 border rounded-md p-6 space-y-7 bg-card">
          <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Search Options</div>

          {allowModeSelect ? (
            <div className="space-y-3">
              <label className="text-xs uppercase tracking-wider text-muted-foreground">Matching Engine</label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger data-testid="mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(modes).map(([k, v]) => (
                    <SelectItem key={k} value={k} data-testid={`mode-option-${k}`}>{k} · {v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-xs font-mono border rounded-sm px-3 py-2 bg-secondary/40" data-testid="auto-engine-badge">
              <Sparkles className="w-3.5 h-3.5 text-brand" /> AUTO matching enabled
            </div>
          )}

          {allowModel && <ModelSelect value={model} onChange={setModel} />}

          <div className="space-y-3">
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Top Results</label>
            <Select value={topN} onValueChange={setTopN}>
              <SelectTrigger data-testid="topn-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-xs uppercase tracking-wider text-muted-foreground">Min Similarity</label>
              <span className="font-mono text-sm font-bold text-brand" data-testid="threshold-value">{threshold}%</span>
            </div>
            <Slider data-testid="similarity-slider" value={[threshold]} onValueChange={(v) => setThreshold(v[0])} min={0} max={100} step={5} />
          </div>

          <div className="flex items-start justify-between gap-3 rounded-md border p-3 bg-secondary/30">
            <div className="space-y-0.5">
              <label htmlFor="remove-bg-toggle" className="text-xs uppercase tracking-wider text-foreground flex items-center gap-1.5 cursor-pointer">
                <Scissors className="w-3.5 h-3.5 text-brand" /> Remove background
              </label>
              <p className="text-[11px] text-muted-foreground leading-snug">
                Strip the background from your photo before searching. Helps with busy backgrounds (a little slower).
              </p>
            </div>
            <Switch id="remove-bg-toggle" data-testid="remove-bg-toggle" checked={removeBg} onCheckedChange={setRemoveBg} />
          </div>

          <Button data-testid="search-btn" onClick={runSearch} disabled={!file || loading}
            className="w-full h-11 gap-2 font-semibold uppercase tracking-wider hover:-translate-y-[1px] transition-transform">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {loading ? "Searching…" : "Search Parts"}
          </Button>
        </div>
      </div>

      {showDebug && debug && (
        <div className="border rounded-md bg-foreground text-background p-6 font-mono text-xs space-y-3" data-testid="debug-panel">
          <div className="text-[11px] uppercase tracking-[0.25em] opacity-60">Debug Information</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-2 gap-x-6">
            <div>Engine: <span className="text-brand">{debug.mode} · {debug.mode_label}</span></div>
            <div>Model filter: <span className="text-brand">{debug.model_filter || "— none —"}</span></div>
            <div>Images searched: <span className="text-brand font-bold">{debug.images_searched}</span></div>
            <div>Candidate pool: {debug.candidate_pool}</div>
            <div>Query views: {debug.query_views}</div>
            <div>Feature time: {debug.feature_ms} ms</div>
            <div>Search time: {debug.search_ms} ms</div>
            <div>Total time: {debug.total_ms} ms</div>
            <div>Threshold: {debug.threshold}%</div>
          </div>
          <div className="pt-1 opacity-80">OCR tokens detected: {debug.ocr_tokens?.length ? debug.ocr_tokens.join(", ") : "— none —"}</div>
        </div>
      )}

      {results && debug?.background_removed && debug?.preview_image && (
        <div className="border rounded-md p-4 bg-card" data-testid="bg-preview-panel">
          <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-3 flex items-center gap-1.5">
            <Scissors className="w-3.5 h-3.5 text-brand" /> Searched with background removed
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <figure className="text-center">
              <div className="w-32 h-32 border rounded-md bg-white flex items-center justify-center overflow-hidden">
                {previewUrl && <img src={previewUrl} alt="original" className="max-h-full max-w-full object-contain" />}
              </div>
              <figcaption className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1.5">Your photo</figcaption>
            </figure>
            <ArrowRight className="w-5 h-5 text-muted-foreground" />
            <figure className="text-center">
              <div className="w-32 h-32 border rounded-md bg-white flex items-center justify-center overflow-hidden">
                <img src={debug.preview_image} alt="background removed" data-testid="bg-preview-image" className="max-h-full max-w-full object-contain" />
              </div>
              <figcaption className="text-[10px] uppercase tracking-wider text-brand mt-1.5">Background removed</figcaption>
            </figure>
          </div>
        </div>
      )}

      {results && (
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <Zap className="w-5 h-5 text-brand" />
            <h2 className="font-heading font-black text-2xl">Top Matching Parts</h2>
            <span className="text-sm text-muted-foreground font-mono">({results.length})</span>
          </div>
          {results.length === 0 ? (
            <div className="border border-dashed rounded-md p-12 text-center text-muted-foreground">
              No strong match found above {threshold}% visual similarity. Try lowering the similarity threshold or removing the model filter.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="results-grid">
              {results.map((r) => <ResultCard key={`${r.part_no}-${r.rank}`} r={r} showScores={showScores} onClick={() => setSelected(r)} />)}
            </div>
          )}
        </div>
      )}

      {selected && (
        <PartDetailDialog open={!!selected} result={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
};

export default VisualSearch;
